import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Check if user exists in our users collection
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // Create new user with default role
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          role: 'user', // Default role
          createdAt: new Date(),
          updatedAt: new Date()
        });
        setUserRole('user');
      } else {
        setUserRole(userDoc.data().role);
      }
      
      return user;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  // Sign in with email/password
  const signInWithEmail = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;
      
      // Get user role
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        setUserRole(userDoc.data().role);
      }
      
      return user;
    } catch (error) {
      console.error('Error signing in with email:', error);
      throw error;
    }
  };

  // Sign up with email/password
  const signUpWithEmail = async (email, password, displayName) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const user = result.user;
      
      // Create user document with default role
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: displayName,
        photoURL: null, // Email/password users don't have photo URL
        role: 'user', // Default role
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      setUserRole('user');
      return user;
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserRole(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  // Update user role (admin only)
  const updateUserRole = async (userId, newRole) => {
    try {
      if (userRole !== 'admin') {
        throw new Error('Only admins can update user roles');
      }
      
      await setDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: new Date()
      }, { merge: true });
      
      // If updating current user's role
      if (userId === currentUser?.uid) {
        setUserRole(newRole);
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return userRole === role;
  };

  // Check if user has admin role
  const isAdmin = () => {
    console.log('isAdmin check:', { userRole, result: userRole === 'admin' });
    return userRole === 'admin';
  };

  // Check if user has user role or higher
  const isUser = () => {
    return userRole === 'user' || userRole === 'admin';
  };

  // Get user photo URL (from auth user or Firestore)
  const getUserPhotoURL = () => {
    return currentUser?.photoURL || null;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', { user: !!user, uid: user?.uid });
      
      if (user) {
        setCurrentUser(user);
        
        // Get user role from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('User data from Firestore:', userData);
            setUserRole(userData.role);
            
            // Update photoURL if it's not in Firestore but available in auth user
            if (!userData.photoURL && user.photoURL) {
              await setDoc(doc(db, 'users', user.uid), {
                photoURL: user.photoURL,
                updatedAt: new Date()
              }, { merge: true });
            }
          } else {
            console.log('Creating new user document');
            // Create user document if it doesn't exist
            await setDoc(doc(db, 'users', user.uid), {
              email: user.email,
              displayName: user.displayName || user.email,
              photoURL: user.photoURL || null,
              role: 'user',
              createdAt: new Date(),
              updatedAt: new Date()
            });
            setUserRole('user');
          }
        } catch (error) {
          console.error('Error getting user role:', error);
          setUserRole('user'); // Default to user role
        }
      } else {
        console.log('User signed out');
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    logout,
    updateUserRole,
    hasRole,
    isAdmin,
    isUser,
    loading,
    getUserPhotoURL
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 